import { setConnectionState } from './connection';
import { isOfflineQueueEnabled } from './flags';
import {
  listOutbox,
  markAttemptFailure,
  removeOutboxItem,
  updateOutboxItem,
} from './outbox';
import { clientMessageIdFromBody } from './queuePolicy';
import type { OutboxItem } from './types';

export type OutboxExecutor = (item: OutboxItem) => Promise<Response>;

export interface SyncLoopOptions {
  platform: string;
  execute: OutboxExecutor;
  intervalMs?: number;
}

/** Browser event after a queued mutation is successfully POSTed (or 409 idempotent). */
export const OFFLINE_SYNCED_EVENT = 'platzona-offline-synced';

export interface OfflineSyncedDetail {
  platform: string;
  outboxId: string;
  idempotencyKey: string;
  method: string;
  path: string;
  body: string | null;
  clientMessageId?: string;
}

const started = new Map<string, () => void>();

function broadcastSynced(platform: string, item: OutboxItem): void {
  if (typeof window === 'undefined') return;
  const detail: OfflineSyncedDetail = {
    platform,
    outboxId: item.id,
    idempotencyKey: item.idempotencyKey,
    method: item.method,
    path: item.path,
    body: item.body,
    clientMessageId: clientMessageIdFromBody(item.body),
  };
  window.dispatchEvent(new CustomEvent(OFFLINE_SYNCED_EVENT, { detail }));
}

async function withLeader<T>(platform: string, fn: () => Promise<T>): Promise<T | undefined> {
  if (typeof navigator === 'undefined' || !navigator.locks?.request) {
    return fn();
  }
  return navigator.locks.request(
    `platzona-offline-sync-${platform}`,
    { ifAvailable: true },
    async (lock) => {
      if (!lock) return undefined;
      return fn();
    },
  );
}

export async function flushOutbox(platform: string, execute: OutboxExecutor): Promise<number> {
  if (!isOfflineQueueEnabled()) return 0;
  const items = await listOutbox(platform, ['pending', 'failed']);
  const now = Date.now();
  const due = items.filter((i) => i.nextAttemptAt <= now);
  const seenEntity = new Set<string>();
  let flushed = 0;

  setConnectionState('syncing');
  try {
    for (const item of due) {
      if (item.entityKey) {
        if (seenEntity.has(item.entityKey)) continue;
        seenEntity.add(item.entityKey);
      }
      await updateOutboxItem(platform, item.id, { status: 'syncing' });
      try {
        const res = await execute(item);
        if (res.ok || res.status === 409) {
          await removeOutboxItem(platform, item.id);
          broadcastSynced(platform, item);
          flushed += 1;
          continue;
        }
        if (res.status === 401 || res.status === 403) {
          await markAttemptFailure(platform, item, `auth ${res.status}`);
          break;
        }
        if (res.status >= 400 && res.status < 500) {
          await updateOutboxItem(platform, item.id, {
            status: 'failed',
            lastError: `HTTP ${res.status}`,
            attempts: item.attempts + 1,
            nextAttemptAt: Date.now() + 3_600_000,
          });
          continue;
        }
        await markAttemptFailure(platform, item, `HTTP ${res.status}`);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        await markAttemptFailure(platform, item, msg);
      }
    }
  } finally {
    setConnectionState(typeof navigator !== 'undefined' && navigator.onLine ? 'online' : 'offline');
  }
  return flushed;
}

/** Start background flush loop (idempotent per platform). */
export function startSyncLoop(options: SyncLoopOptions): () => void {
  const existing = started.get(options.platform);
  if (existing) return existing;

  // SSR / Node vitest without fake-indexedDB: no-op stop handle
  if (typeof indexedDB === 'undefined') {
    const noop = () => {
      started.delete(options.platform);
    };
    started.set(options.platform, noop);
    return noop;
  }

  let stopped = false;
  const intervalMs = options.intervalMs ?? 15_000;

  const tick = async () => {
    if (stopped || !isOfflineQueueEnabled()) return;
    await withLeader(options.platform, () => flushOutbox(options.platform, options.execute));
  };

  const onOnline = () => {
    void tick();
  };
  const onVisible = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') void tick();
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);
  }

  const timer = setInterval(() => void tick(), intervalMs);
  void tick();

  const stop = () => {
    stopped = true;
    clearInterval(timer);
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisible);
    }
    started.delete(options.platform);
  };

  started.set(options.platform, stop);
  return stop;
}
